import { useEffect, useState, useCallback } from "react";

import { useQlickupAPI, QlickupActions, AdditionalParams } from "0.Mine/1.Hooks/APICall";
import { Email, EmailMessagingMultiple, EmailMessagingSingle } from "0.Mine/3.Interfaces/Email";
import { SocialMediaIdentifier } from "0.Mine/3.Interfaces/social";
import {
  WhatsappConversationResponse,
  WhatsappResponse,
} from "0.Mine/3.Interfaces/whatsappResponse";
import WebSocketService from "0.Mine/services/websocket";

export const useOmniChat = (businessId?: string) => {
  const { QlickUpAPI, QlickUpAPIWithFormData } = useQlickupAPI();
  const [isConnected, setIsConnected] = useState(false);
  const [wsError, setWsError] = useState<string | null>(null);
  const ws = WebSocketService.getInstance();

  useEffect(() => {
    if (!businessId) return;

    // Connect to WebSocket when the component mounts
    ws.connect(businessId);

    // Setup WebSocket listeners
    const handleMessage = (message: any) => {
      if (message.type === "error") {
        setWsError(message.payload.message);
      }
    };

    ws.addListener(handleMessage);
    setIsConnected(ws.getConnectionStatus());

    // Cleanup on unmount
    return () => {
      ws.removeListener(handleMessage);
      ws.disconnect();
    };
  }, [businessId]);

  const retrieveAllEmails = async (businessId: number) => {
    try {
      const params: AdditionalParams = {
        limit: 10,
        offset: 0,
        stream: 1,
        nocache: 1,
      };
      const response = await QlickUpAPI(
        businessId.toString(),
        "getall",
        null,
        "email_retrieve",
        QlickupActions.GET,
        params
      );

      const messages = response as EmailMessagingMultiple;
      if (messages.success) {
        return messages.emails;
      } else {
        console.error(messages.message);
      }
    } catch (error) {
      console.error("Error retrieving emails:", error);
    }
  };

  const retrieveConversation = async (businessId: number, emailId: string) => {
    try {
      const params: AdditionalParams = {
        email_id: emailId,
      };

      const response = await QlickUpAPI(
        businessId.toString(),
        "getone",
        null,
        "email_retrieve",
        QlickupActions.GET,
        params
      );

      const messages = response as EmailMessagingSingle;
      if (messages.success) {
        return messages.email;
      } else {
        console.error(messages.message);
      }
    } catch (error) {
      console.error("Error retrieving emails:", error);
    }
  };

  const retrieveMetaConversations = async (businessId: number, platform: SocialMediaIdentifier) => {
    try {
      const params: AdditionalParams = {
        platform: SocialMediaIdentifier[platform].toLowerCase(),
      };

      const response = await QlickUpAPI(
        businessId.toString(),
        "getFBMessages",
        null,
        "connectsocial",
        QlickupActions.GET,
        params
      );

      return response.conversations;
    } catch (error) {
      console.error("Error retrieving messages:", error);
      return null;
    }
  };

  const retrieveMetaConversation = async (
    businessId: number,
    platform: SocialMediaIdentifier,
    conversationId: string
  ) => {
    try {
      const params: AdditionalParams = {
        platform: SocialMediaIdentifier[platform].toLowerCase(),
        conversation_id: conversationId,
      };

      const response = await QlickUpAPI(
        businessId.toString(),
        "getFBMessages",
        null,
        "connectsocial",
        QlickupActions.GET,
        params
      );

      return response;
    } catch (error) {
      console.error("Error retrieving messages:", error);
      return null;
    }
  };

  const sendSocialMediaMessage = async (
    businessId: number,
    platform: string,
    conversationId: string,
    message: string,
    recipient: string
  ) => {
    try {
      // If WebSocket is connected, send through WebSocket
      if (isConnected) {
        ws.send({
          type: "message",
          payload: {
            businessId,
            platform,
            conversationId,
            message,
            recipient,
          },
        });
        return { success: true };
      }

      // Fallback to HTTP if WebSocket is not connected
      const formData = new FormData();
      formData.append("provider", platform);
      formData.append("businessid", businessId.toString());
      formData.append("conversation_id", conversationId);
      formData.append("message", message);
      formData.append("recipient", recipient);
      formData.append("text", message);

      const response = await QlickUpAPIWithFormData(
        businessId.toString(),
        "replydm",
        null,
        "metricool",
        QlickupActions.POST,
        formData
      );

      return response;
    } catch (error) {
      console.error("Error sending message:", error);
      return { success: false, error };
    }
  };

  const startWhatsappSession = async (businessId: number) => {
    try {
      const response = await QlickUpAPI(
        businessId.toString(),
        "getQr",
        null,
        "wa_messages",
        QlickupActions.GET
      );
      return response;
    } catch (error) {
      console.error("Error starting WhatsApp session:", error);
      return null;
    }
  };

  const retrieveWhatsappMessages = async (businessId: number) => {
    try {
      const response = await QlickUpAPI(
        businessId.toString(),
        "getContacts",
        null,
        "wa_messages",
        QlickupActions.GET
      );
      return response as WhatsappResponse;
    } catch (error) {
      console.error("Error retrieving WhatsApp messages:", error);
      return null;
    }
  };

  const replyToReview = async (businessId: number, reviewId: string, message: string) => {
    try {
      const params: AdditionalParams = {
        reviewid: reviewId,
        text: message,
        provider: "GMB",
      };

      const response = await QlickUpAPI(
        businessId.toString(),
        "replyTo",
        null,
        "metricool",
        QlickupActions.POST,
        params
      );

      return response;
    } catch (error) {
      console.error("Error replying to review:", error);
      return null;
    }
  };

  const retrieveWhatsappConversation = async (businessId: number, conversationId: string) => {
    try {
      const additionalParams: AdditionalParams = {
        chatid: conversationId,
      };

      const response = await QlickUpAPI(
        businessId.toString(),
        "getmessages",
        null,
        "wa_messages",
        QlickupActions.GET,
        additionalParams
      );

      return response as WhatsappConversationResponse;
    } catch (error) {
      console.error("Error retrieving WhatsApp conversation:", error);
      return null;
    }
  };

  const sendWhatsappMessage = async (
    businessId: number,
    conversationId: string,
    message: string
  ) => {
    try {
      if (isConnected) {
        ws.send({
          type: "whatsapp_message",
          payload: {
            businessId,
            conversationId,
            message,
          },
        });
        return { success: true };
      }

      const formData = new FormData();
      formData.append("to", conversationId);
      formData.append("message", message);

      const response = await QlickUpAPIWithFormData(
        businessId.toString(),
        "sendmessage",
        null,
        "wa_messages",
        QlickupActions.POST,
        formData
      );
      return response;
    } catch (error) {
      console.error("Error sending WhatsApp message:", error);
      return { success: false, error };
    }
  };

  return {
    retrieveAllEmails,
    retrieveConversation,
    retrieveMetaConversations,
    retrieveMetaConversation,
    sendSocialMediaMessage,
    startWhatsappSession,
    retrieveWhatsappMessages,
    replyToReview,
    retrieveWhatsappConversation,
    sendWhatsappMessage,
    isConnected,
    wsError,
  };
};
